import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDonation } from '../../contexts/DonationContext';
import { getCurrentUser } from '../../services/authService';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import DonateOptionsModal from '../DonateOptionsModal/DonateOptionsModal';
import images from '../../data/images.json';
import './Header.css';

const Header: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { selectedCause } = useDonation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDonateModalOpen, setIsDonateModalOpen] = useState(false);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const isHomePage = location.pathname === '/';
  const isDashboardPage = location.pathname.startsWith('/dashboard');

  const handleDonateClick = () => {
    setIsDonateModalOpen(true);
  };

  const closeDonateModal = () => {
    setIsDonateModalOpen(false);
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  // Check if user is logged in
  useEffect(() => {
    const user = getCurrentUser();
    setIsUserLoggedIn(!!user);
  }, [location.pathname]);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  return (
    <>
      <header className={`header ${isHomePage ? 'home-page' : ''}`}>
        <nav className="header-nav">
          <div className="container">
            <div className="nav-content">

              {/* Desktop Navigation Menu */}
              <ul className="nav-menu desktop-menu">
                <li>
                  <Link
                    to="/"
                    className={isActive('/') ? 'active' : ''}
                  >
                    {t('header.nav.home')}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/causes"
                    className={isActive('/causes') ? 'active' : ''}
                  >
                    {t('header.nav.causes')}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/events"
                    className={isActive('/events') ? 'active' : ''}
                  >
                    {t('header.nav.events')}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/gallery"
                    className={isActive('/gallery') ? 'active' : ''}
                  >
                    {t('header.nav.gallery')}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/manjushree"
                    className={isActive('/manjushree') ? 'active' : ''}
                  >
                    {t('header.nav.manjushree')}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/contact"
                    className={isActive('/contact') ? 'active' : ''}
                  >
                    {t('header.nav.contact')}
                  </Link>
                </li>
              </ul>

              {/* Language Switcher - Desktop */}
              <div className="nav-language-switcher">
                <LanguageSwitcher />
              </div>
            </div>
          </div>
        </nav>

        <div className="header-main">
          <div className="container">
            <div className="header-main-content">
              {/* Hamburger Menu Button - Mobile Only */}
              <button
                className="hamburger-menu-button"
                onClick={toggleMobileMenu}
                aria-label="Toggle menu"
              >
                <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
                <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
                <span className={`hamburger-line ${isMobileMenuOpen ? 'open' : ''}`}></span>
              </button>

              <Link to="/" className="logo-link">
                <div className="logo-wrapper">
                  <img
                    src={images.logo}
                    alt="Manjushree Foundation Logo"
                    className="logo-img"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = 'https://via.placeholder.com/60x60/f4c430/333333?text=SW';
                    }}
                  />
                </div>
                <div className="logo-text">
                  <h1 className="logo-name">{t('header.logo.name')}</h1>
                </div>
              </Link>
              {!(isUserLoggedIn && isDashboardPage) && (
                <button
                  className="donate-button-header desktop-donate-button"
                  onClick={handleDonateClick}
                >
                  <span className="heart-icon">❤️</span>
                  {t('header.donateButton')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <div
          className={`mobile-menu-overlay ${isMobileMenuOpen ? 'open' : ''}`}
          onClick={closeMobileMenu}
        >
          <div className="mobile-menu-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="mobile-menu-close"
              onClick={closeMobileMenu}
              aria-label="Close menu"
            >
              ×
            </button>
            <ul className="mobile-nav-menu">
              <li>
                <Link
                  to="/"
                  className={isActive('/') ? 'active' : ''}
                  onClick={closeMobileMenu}
                >
                  {t('header.nav.home')}
                </Link>
              </li>
              <li>
                <Link
                  to="/causes"
                  className={isActive('/causes') ? 'active' : ''}
                  onClick={closeMobileMenu}
                >
                  {t('header.nav.causes')}
                </Link>
              </li>
              <li>
                <Link
                  to="/events"
                  className={isActive('/events') ? 'active' : ''}
                  onClick={closeMobileMenu}
                >
                  {t('header.nav.events')}
                </Link>
              </li>
              <li>
                <Link
                  to="/gallery"
                  className={isActive('/gallery') ? 'active' : ''}
                  onClick={closeMobileMenu}
                >
                  {t('header.nav.gallery')}
                </Link>
              </li>
              <li>
                <Link
                  to="/manjushree"
                  className={isActive('/manjushree') ? 'active' : ''}
                  onClick={closeMobileMenu}
                >
                  {t('header.nav.manjushree')}
                </Link>
              </li>
              <li>
                <Link
                  to="/contact"
                  className={isActive('/contact') ? 'active' : ''}
                  onClick={closeMobileMenu}
                >
                  {t('header.nav.contact')}
                </Link>
              </li>
            </ul>

            {/* Language Switcher - Mobile */}
            <div className="mobile-language-switcher">
              <LanguageSwitcher />
            </div>
            {!(isUserLoggedIn && isDashboardPage) && (
              <button
                className="mobile-donate-button"
                onClick={() => {
                  closeMobileMenu();
                  handleDonateClick();
                }}
              >
                <span className="heart-icon">❤️</span>
                {t('header.donateButton')}
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Donate Options Modal */}
      <DonateOptionsModal
        isOpen={isDonateModalOpen}
        onClose={closeDonateModal}
      />
    </>
  );
};

export default Header;

